/**
 * Department Benchmarking Analytics Component
 * Phase 2: Team & Department Analytics
 */

import React from 'react';
import { 
  Building2, 
  TrendingUp, 
  Target, 
  DollarSign, 
  Users,
  BarChart3,
  Award,
  Activity
} from 'lucide-react';
import { DepartmentBenchmark as DepartmentBenchmarkType } from '../../services/analytics';

interface DepartmentBenchmarkingProps {
  data: DepartmentBenchmarkType[];
  loading?: boolean;
}

export const DepartmentBenchmarking: React.FC<DepartmentBenchmarkingProps> = ({ 
  data, 
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return `€${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `€${(amount / 1000).toFixed(1)}K`;
    }
    return `€${amount.toFixed(0)}`;
  };

  const formatNumber = (value: number | string | null | undefined, decimals: number = 1): string => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const getPerformanceColor = (rank: number, total: number) => {
    const percentile = (rank / total) * 100;
    if (percentile <= 25) return 'text-green-600';
    if (percentile <= 50) return 'text-blue-600';
    if (percentile <= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (rank: number, total: number) => {
    const percentile = (rank / total) * 100;
    if (percentile <= 25) return { text: 'Top Performer', color: 'bg-green-100 text-green-800' };
    if (percentile <= 50) return { text: 'Good', color: 'bg-blue-100 text-blue-800' };
    if (percentile <= 75) return { text: 'Average', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Needs Improvement', color: 'bg-red-100 text-red-800' };
  };

  return (
    <div className="space-y-6">
      {/* Department Performance Overview */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Department Performance</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Building2 className="h-4 w-4" />
            <span>{data.length} departments</span>
          </div>
        </div>

        {/* Department Performance Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Workshops
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completion Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Savings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((dept) => {
                const performanceBadge = getPerformanceBadge(dept.performance_rank, data.length);
                return (
                  <tr key={dept.department_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-lg font-bold ${getPerformanceColor(dept.performance_rank, data.length)}`}>
                          #{dept.performance_rank}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {dept.department_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dept.total_workshops}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatNumber(dept.completion_rate)}%
                        </div>
                        <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${Math.min(dept.completion_rate, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatNumber(dept.average_progress)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(dept.total_released_savings)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${performanceBadge.color}`}>
                        {performanceBadge.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Average Completion Rate */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Completion Rate</p>
              <p className="text-3xl font-bold text-green-600">
                {formatNumber(data.reduce((sum, dept) => sum + dept.completion_rate, 0) / data.length)}
                %
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Total Workshops */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Workshops</p>
              <p className="text-3xl font-bold text-blue-600">
                {data.reduce((sum, dept) => sum + dept.total_workshops, 0)}
              </p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Total Savings */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Savings</p>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(data.reduce((sum, dept) => sum + dept.total_released_savings, 0))}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Resource Utilization */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Resource Utilization</p>
              <p className="text-3xl font-bold text-purple-600">
                {formatNumber(data.reduce((sum, dept) => sum + dept.resource_utilization, 0) / data.length)}
                %
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Department Performance Comparison */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Comparison</h3>
        <div className="space-y-4">
          {data.slice(0, 5).map((dept) => (
            <div key={dept.department_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                  <span className="text-sm font-semibold text-blue-600">#{dept.performance_rank}</span>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{dept.department_name}</h4>
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span>{dept.total_workshops} workshops</span>
                    <span>{formatNumber(dept.completion_rate)}% completion</span>
                    <span>{formatCurrency(dept.total_released_savings)} savings</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">
                    {formatNumber(dept.average_progress)}%
                  </div>
                  <div className="text-xs text-gray-600">Avg Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">
                    {formatNumber(dept.goal_achievement_rate)}%
                  </div>
                  <div className="text-xs text-gray-600">Goal Achievement</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-purple-600">
                    {formatNumber(dept.resource_utilization)}%
                  </div>
                  <div className="text-xs text-gray-600">Resource Utilization</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


